import { useEffect, useState } from 'react';
import type { ApiClient, RsInhalt, RsModell, RsModellInfo, RsServer } from '../../api';
import { ContentSection, EmptyState, ErrorState, Feld, StatusBadge } from '../../muster';
import { GrundDialog, type Rueckfrage } from '../acc/AccZuordnung';
import { zeitpunkt } from '../acc/acczeit';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * „MODELL VOM REVIT SERVER HINZUFÜGEN“ (RS1b, Auftrag des Eigentümers F436 vom 03.11.2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Muster „ACC durchsuchen“: Server wählen → Ordner aufklappen → Modell prüfen → mit Grund übernehmen. TGAPlan liest
 * den Revit Server nur (feste GET-Liste auf dem Server); die Identität des Modells ist seine ModelGUID, der Pfad nur
 * Anzeige. Ist die Anbindung aus (Schalter `RevitServer:Aktiv`) oder fehlt das Recht, erscheint der Abschnitt nicht.
 */

type Zweig = { zustand: 'laedt' } | { zustand: 'fehler'; text: string } | { zustand: 'da'; inhalt: RsInhalt };
type Pruefung = 'laedt' | string | RsModellInfo;

/** Die Meldung des Servers; 403 heißt hier: keine Rolle in TGAPlan. */
export function rsFehlertext(e: unknown): string {
  const t = e instanceof Error ? e.message : String(e);
  return t === 'HTTP 403' ? 'Den Revit Server durchsuchen dürfen nur ProjectAdmin und SystemAdmin.' : t;
}

/** „12,3 MB“; ohne Angabe leer. */
export function groesseText(byte: number | null | undefined): string {
  if (byte === null || byte === undefined || byte < 0) return '';
  return `${(byte / (1024 * 1024)).toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} MB`;
}

/** Pfad aus Ordnerpfad und Name, getrennt mit „/“ (der Server bekommt ihn segmentweise geprüft). */
export function pfadVon(ordner: string, name: string): string {
  return ordner ? `${ordner}/${name}` : name;
}

/** Modellname ohne „.rvt“ - so legt der Server ihn an. */
export function modellname(datei: string): string {
  return datei.toLowerCase().endsWith('.rvt') ? datei.slice(0, -4).trim() : datei.trim();
}

export function RevitServerHinzufuegen({ api, projektId, beiUebernahme }: {
  api: ApiClient;
  projektId: string;
  /** Nach einer Übernahme (z. B. Modellliste neu laden). */
  beiUebernahme?: () => void;
}) {
  const [server, setServer] = useState<RsServer[] | null | 'laedt'>('laedt');
  const [ladefehler, setLadefehler] = useState<string | null>(null);
  const [kennung, setKennung] = useState('');
  const [zweige, setZweige] = useState<Record<string, Zweig>>({});
  const [pruefungen, setPruefungen] = useState<Record<string, Pruefung>>({});
  const [rueckfrage, setRueckfrage] = useState<Rueckfrage | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const antwort = await api.revitServerListe();
        if (!aktiv) return;
        const liste = Array.isArray(antwort) ? antwort : null;
        setServer(liste);
        if (liste && liste.length === 1) setKennung(liste[0].kennung);
      } catch (e) {
        if (!aktiv) return;
        // Ohne Recht (403) oder ohne Anbindung bleibt der Abschnitt weg - nur echte Störungen werden gezeigt.
        const t = rsFehlertext(e);
        if (e instanceof Error && e.message === 'HTTP 403') setServer(null);
        else { setServer([]); setLadefehler(t); }
      }
    })();
    return () => { aktiv = false; };
  }, [api]);

  if (server === null || server === 'laedt') return null;
  const gewaehlt = server.find((s) => s.kennung === kennung) ?? null;

  const serverWaehlen = (k: string) => {
    setKennung(k);
    setZweige({});
    setPruefungen({});
    setMeldung(null);
  };

  const ordnerLaden = async (pfad: string) => {
    if (!gewaehlt) return;
    if (zweige[pfad] && pfad !== '') {
      setZweige((z) => { const n = { ...z }; delete n[pfad]; return n; });
      return;
    }
    setZweige((z) => ({ ...z, [pfad]: { zustand: 'laedt' } }));
    try {
      const inhalt = await api.revitServerInhalt(gewaehlt.kennung, pfad);
      setZweige((z) => ({ ...z, [pfad]: { zustand: 'da', inhalt } }));
    } catch (e) {
      setZweige((z) => ({ ...z, [pfad]: { zustand: 'fehler', text: rsFehlertext(e) } }));
    }
  };

  const pruefen = async (pfad: string) => {
    if (!gewaehlt) return;
    setPruefungen((p) => ({ ...p, [pfad]: 'laedt' }));
    try {
      const info = await api.revitServerModell(gewaehlt.kennung, pfad);
      setPruefungen((p) => ({ ...p, [pfad]: info }));
    } catch (e) {
      setPruefungen((p) => ({ ...p, [pfad]: rsFehlertext(e) }));
    }
  };

  const uebernehmen = (pfad: string, info: RsModellInfo) => {
    if (!gewaehlt) return;
    const name = modellname(pfad.split('/').pop() ?? pfad);
    setRueckfrage({
      titel: `„${name}“ vom Revit Server übernehmen?`,
      text: `Quelle Revit Server ${gewaehlt.anzeigename} (Revit ${gewaehlt.jahr}), Modellschlüssel ${info.modelGuid}. `
        + 'TGAPlan liest das Modell nur und ändert auf dem Revit Server nichts.',
      bestaetigen: 'Übernehmen',
      aktion: async (grund) => {
        const r = await api.revitServerUebernehmen(projektId, { server: gewaehlt.kennung, pfad, grund });
        setRueckfrage(null);
        setMeldung(`Modell „${r.name}“ angelegt (Revit ${r.revitVersion}).`);
        beiUebernahme?.();
      },
    });
  };

  const modellZeile = (ordner: string, m: RsModell, tiefe: number) => {
    const pfad = pfadVon(ordner, m.name);
    const p = pruefungen[pfad];
    return (
      <li key={`m:${pfad}`} data-modell={m.name} style={{ marginLeft: `${tiefe}em` }}>
        <span className="acc-datei">{m.name}</span>{' '}
        <span className="sehr-schwach">{[groesseText(m.groesse), m.produktversion].filter(Boolean).join(' · ')}</span>{' '}
        {m.sperrstatus !== null && m.sperrstatus !== 0 && <StatusBadge zustand="warnung" klein text="gesperrt" />}{' '}
        <button className="m-knopf" onClick={() => pruefen(pfad)} disabled={p === 'laedt'}>Prüfen</button>
        {p === 'laedt' && <span className="sehr-schwach"> lädt …</span>}
        {typeof p === 'string' && p !== 'laedt' && <ErrorState klein meldung={p} />}
        {p && typeof p === 'object' && (
          <span data-pruefung>
            {' '}<StatusBadge zustand="info" klein text="Modellschlüssel" titel={p.modelGuid} />{' '}
            <code className="sehr-schwach">{p.modelGuid}</code>{' '}
            <span className="sehr-schwach">
              {p.versionen} Version(en){p.geaendert ? ` · geändert ${zeitpunkt(p.geaendert)}` : ''}
            </span>{' '}
            <button className="m-knopf"onClick={() => uebernehmen(pfad, p)}>Übernehmen</button>
          </span>
        )}
      </li>
    );
  };

  const inhaltListe = (ordner: string, tiefe: number) => {
    const z = zweige[ordner];
    if (!z) return null;
    if (z.zustand === 'laedt') return <span className="sehr-schwach"> lädt …</span>;
    if (z.zustand === 'fehler') return <ErrorState klein meldung={z.text} />;
    return (
      <ul className="acc-baum" data-baum={ordner === '' ? '' : undefined}>
        {z.inhalt.ordner.map((o) => {
          const pfad = pfadVon(ordner, o.name);
          return (
            <li key={`o:${pfad}`} data-ordner={o.name}>
              <button className="m-knopf acc-ordner" aria-expanded={!!zweige[pfad]} onClick={() => ordnerLaden(pfad)}
                      style={{ marginLeft: `${tiefe}em` }}>
                {zweige[pfad] ? '▾' : '▸'} {o.name}
              </button>
              {inhaltListe(pfad, tiefe + 1)}
            </li>
          );
        })}
        {z.inhalt.modelle.map((m) => modellZeile(ordner, m, tiefe))}
        {z.inhalt.ordner.length === 0 && z.inhalt.modelle.length === 0 && (
          <li className="sehr-schwach" style={{ marginLeft: `${tiefe}em` }}>leer</li>
        )}
      </ul>
    );
  };

  return (
    <ContentSection
      titel="Modell vom Revit Server hinzufügen"
      beschreibung={'Nur lesend: Ordner und Modelle des Revit Servers. Ein übernommenes Modell erkennt TGAPlan an seinem '
        + 'Modellschlüssel (ModelGUID), nicht am Pfad.'}
      aktionen={gewaehlt && (
        <button className="m-knopf" onClick={() => ordnerLaden('')} disabled={zweige['']?.zustand === 'laedt'}>
          {zweige[''] ? 'Neu laden' : 'Durchsuchen'}
        </button>
      )}
    >
      {ladefehler && <ErrorState klein meldung={ladefehler} />}
      {meldung && <p role="status" data-meldung><StatusBadge zustand="gut" text={meldung} /></p>}
      {!ladefehler && server.length === 0 && (
        <EmptyState klein titel="Kein Revit Server eingetragen"
                    erklaerung="Das Register steht in der Konfiguration des Servers (RevitServer:Server)." />
      )}
      {server.length > 0 && (
        <Feld art="auswahl" beschriftung="Revit Server" wert={kennung} platzhalter="Server wählen …"
              optionen={server.map((s) => ({ wert: s.kennung, beschriftung: `${s.anzeigename} (Revit ${s.jahr})` }))}
              beiAenderung={(w) => serverWaehlen(w as string)} />
      )}
      {inhaltListe('', 0)}
      <GrundDialog rueckfrage={rueckfrage} beiAbbrechen={() => setRueckfrage(null)} />
    </ContentSection>
  );
}
